import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Bot, Send, X, Sparkles, Loader2, History, Plus, MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  'Como começo minha trilha no Revit?',
  'Qual a diferença entre worksets e links no Revit?',
  'Onde vejo meus certificados na plataforma?',
];

export const AssistantWidget = () => {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pendingContext, setPendingContext] = useState(null);
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, loading, typing]);

  // Abrir o assistente com contexto de um módulo/curso (evento global)
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      setOpen(true);
      setShowHistory(false);
      setMessages([]);
      setSessionId(null);
      if (detail.context) setPendingContext(detail.context);
      if (detail.starter) setInput(detail.starter);
    };
    window.addEventListener('open-assistant', handler);
    return () => window.removeEventListener('open-assistant', handler);
  }, []);

  if (!isAuthenticated) return null;

  const openHistory = async () => {
    setShowHistory(true);
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/assistant/sessions`, { withCredentials: true });
      setSessions(res.data || []);
    } catch (e) {
      setSessions([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadSession = async (sid) => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/assistant/sessions/${sid}`, { withCredentials: true });
      setMessages((res.data.messages || []).map((m) => ({ role: m.role, content: m.content })));
      setSessionId(sid);
      setShowHistory(false);
    } catch (e) {
      // keep history open on error
    } finally {
      setLoadingHistory(false);
    }
  };

  const newConversation = () => {
    setMessages([]);
    setSessionId(null);
    setShowHistory(false);
  };

  const send = async (text, ctxOverride) => {
    const content = (text ?? input).trim();
    if (!content || loading || typing) return;
    const useCtx = ctxOverride ?? pendingContext;
    setInput('');
    setPendingContext(null);
    setMessages((prev) => [...prev, { role: 'user', content }]);
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/assistant/chat`,
        { message: content, session_id: sessionId, context: useCtx || undefined },
        { withCredentials: true }
      );
      if (!mountedRef.current) return;
      setSessionId(res.data.session_id);
      const full = res.data.answer || '';
      // Efeito "máquina de escrever" — revela a resposta palavra por palavra
      setLoading(false);
      setTyping(true);
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);
      const tokens = full.split(/(\s+)/);
      let acc = '';
      for (let i = 0; i < tokens.length; i++) {
        if (!mountedRef.current) return;
        acc += tokens[i];
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: acc };
          return copy;
        });
        // pausa curta apenas em tokens não-espaço
        if (tokens[i].trim()) {
          await new Promise((r) => setTimeout(r, 18));
        }
      }
      setTyping(false);
    } catch (e) {
      if (!mountedRef.current) return;
      setLoading(false);
      setTyping(false);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Desculpe, tive um problema para responder agora. Tente novamente em instantes.' },
      ]);
    }
  };

  return (
    <>
      {/* Botão flutuante */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-open-btn"
          aria-label="Abrir Assistente IA"
          className="fixed bottom-6 left-6 z-40 flex items-center gap-2 bg-gradient-to-br from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white rounded-full pl-3 pr-4 py-3 shadow-xl shadow-red-900/40 transition-all duration-300 hover:scale-105"
        >
          <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white/15">
            <Bot size={18} />
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-400 ring-2 ring-red-700" />
          </span>
          <span className="text-sm font-semibold hidden sm:block">Assistente IA</span>
        </button>
      )}

      {/* Painel de chat */}
      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-6 left-6 z-40 w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] flex flex-col bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
          {/* Cabeçalho */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-red-700 to-red-900">
            <div className="flex items-center gap-2 text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15">
                <Sparkles size={16} />
              </span>
              <div className="leading-tight">
                <div className="text-sm font-bold">Assistente AJ | BIM</div>
                <div className="text-[10px] text-red-100/80">Powered by ChatGPT (GPT-5.6)</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={newConversation}
                data-testid="assistant-new-btn"
                aria-label="Nova conversa"
                title="Nova conversa"
                className="text-white/80 hover:text-white transition-colors p-1"
              >
                <Plus size={18} />
              </button>
              <button
                onClick={showHistory ? () => setShowHistory(false) : openHistory}
                data-testid="assistant-history-btn"
                aria-label="Histórico"
                title="Histórico de conversas"
                className={`transition-colors p-1 ${showHistory ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                <History size={18} />
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="assistant-close-btn"
                aria-label="Fechar"
                className="text-white/80 hover:text-white transition-colors p-1"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Histórico de conversas */}
          {showHistory ? (
            <div className="flex-1 overflow-y-auto px-3 py-4 bg-zinc-950/40" data-testid="assistant-history-panel">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-white">Conversas anteriores</h4>
                <button
                  onClick={newConversation}
                  className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                  data-testid="assistant-history-new"
                >
                  <Plus size={14} /> Nova
                </button>
              </div>
              {loadingHistory ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm py-6 justify-center">
                  <Loader2 size={16} className="animate-spin" /> Carregando...
                </div>
              ) : sessions.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">Você ainda não tem conversas salvas.</p>
              ) : (
                <div className="space-y-2">
                  {sessions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => loadSession(s.id)}
                      data-testid="assistant-history-item"
                      className={`w-full text-left rounded-lg px-3 py-2.5 border transition-colors ${
                        s.id === sessionId
                          ? 'bg-red-600/20 border-red-500/50'
                          : 'bg-zinc-800 hover:bg-zinc-700 border-zinc-700'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <MessageSquare size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm text-gray-100 truncate">{s.title || 'Conversa'}</div>
                          <div className="text-[10px] text-gray-500">
                            {s.updated_at ? new Date(s.updated_at).toLocaleString('pt-BR') : ''}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
          <>
          {/* Mensagens */}
          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-zinc-950/40">
            {messages.length === 0 && (
              <div className="text-center px-2 py-6">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-600/20">
                  <Bot className="text-red-400" size={24} />
                </div>
                <p className="text-gray-300 text-sm font-medium mb-1">Olá! Sou seu assistente de estudos BIM.</p>
                <p className="text-gray-500 text-xs mb-4">Tire dúvidas de Revit, BIM e da plataforma.</p>
                <button
                  onClick={() => send('Com base nas minhas matrículas, qual o próximo módulo que devo estudar e por quê?')}
                  data-testid="assistant-nextstep"
                  className="w-full text-left text-xs text-white bg-gradient-to-r from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 rounded-lg px-3 py-2.5 mb-3 flex items-center gap-2 transition-colors"
                >
                  <Sparkles size={14} className="flex-shrink-0" />
                  Sugira meu próximo passo nos estudos
                </button>
                <div className="space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid="assistant-suggestion"
                      className="w-full text-left text-xs text-gray-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg px-3 py-2 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`assistant-msg-${m.role}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-red-600 text-white rounded-br-sm'
                      : 'bg-zinc-800 text-gray-100 rounded-bl-sm border border-zinc-700'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start" data-testid="assistant-loading">
                <div className="bg-zinc-800 border border-zinc-700 rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2 text-gray-400 text-sm">
                  <Loader2 size={16} className="animate-spin" />
                  Pensando...
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Entrada */}
          <div className="border-t border-zinc-800 p-2.5 bg-zinc-900">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Escreva sua dúvida..."
                data-testid="assistant-input"
                className="flex-1 resize-none max-h-28 bg-zinc-800 text-white text-sm rounded-xl px-3 py-2.5 border border-zinc-700 focus:outline-none focus:border-red-500 placeholder:text-gray-500"
              />
              <button
                onClick={() => send()}
                disabled={loading || typing || !input.trim()}
                data-testid="assistant-send-btn"
                aria-label="Enviar"
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
              >
                <Send size={18} />
              </button>
            </div>
            <p className="text-[10px] text-gray-600 text-center mt-1.5">
              A IA pode cometer erros. Confira informações importantes.
            </p>
          </div>
          </>
          )}
        </div>
      )}
    </>
  );
};

export default AssistantWidget;
